import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link2, Plus, Copy, Check, Eye, XCircle, Calendar, TrendingUp, Layers, Building2, Landmark, Trash2, Send, UserPlus, X as XIcon, ArrowRight } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'
import { isStaffViewer, markStaffViewer, clearStaffViewer, autoMarkStaffViewer } from '../../lib/staffViewer'

// Central preview-link management. One place to create trial URLs for any
// company (single-site preview) or client group (head-office / group-wide
// preview showing all sites combined). Shows engagement (view counts) and
// makes revocation quick.
export default function PreviewLinks() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState([])
  const [groups, setGroups] = useState([]) // groups with >1 site — only these get "head office" previews
  const [links, setLinks] = useState([])
  const [loading, setLoading] = useState(false)
  const [newCompanyId, setNewCompanyId] = useState('')
  const [newCompanyLabel, setNewCompanyLabel] = useState('')
  const [creatingCompany, setCreatingCompany] = useState(false)
  const [newGroupId, setNewGroupId] = useState('')
  const [newGroupLabel, setNewGroupLabel] = useState('')
  const [creatingGroup, setCreatingGroup] = useState(false)
  const [copiedToken, setCopiedToken] = useState(null)
  // "Go live" — convert a preview link into a real client invite
  const [goLive, setGoLive] = useState(null) // { link, name, email, sending, result }
  // Staff view-suppression: IPs whose preview visits shouldn't inflate counts
  const [staffIps, setStaffIps] = useState([])
  // ...and the same suppression at browser level, which follows the person
  // rather than the address so it still works from home or a phone.
  const [browserMarked, setBrowserMarked] = useState(() => isStaffViewer())
  const [newIp, setNewIp] = useState('')
  const [newIpDesc, setNewIpDesc] = useState('')
  const [ipBusy, setIpBusy] = useState(false)
  const [detectedIp, setDetectedIp] = useState('')

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    const [cRes, gRes, lRes] = await Promise.all([
      supabase.from('companies')
        .select('id, name, client_group_id, client_groups(id, name)')
        .is('archived_at', null)
        .order('name'),
      supabase.from('client_groups')
        .select('id, name, companies(id)')
        .is('archived_at', null)
        .order('name'),
      supabase.from('preview_links')
        .select('*, companies(id, name, client_groups(name)), client_groups(id, name)')
        .order('created_at', { ascending: false }),
    ])
    setCompanies(cRes.data || [])
    // Only surface groups with 2+ member sites — a "head office" link makes no
    // sense for a single-site client.
    setGroups((gRes.data || []).filter(g => (g.companies || []).length > 1))
    setLinks(lRes.data || [])
    // Staff IPs list for view-suppression
    const { data: ipsData } = await supabase.from('staff_view_ips').select('*').order('added_at', { ascending: false })
    setStaffIps(ipsData || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  // Detect the caller's own public IP once — used by "Add my current IP".
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('https://api.ipify.org?format=json')
        const j = await res.json()
        if (!cancelled) setDetectedIp(j?.ip || '')
      } catch (_) { /* offline / blocked — the manual entry field still works */ }
    })()
    return () => { cancelled = true }
  }, [])

  // Reaching this page requires a staff login, so that alone is enough to
  // mark the browser — no one has to think about IP addresses. Respects a
  // deliberate unmark (see lib/staffViewer).
  useEffect(() => {
    if (autoMarkStaffViewer()) setBrowserMarked(true)
  }, [])

  const toggleBrowserMark = () => {
    if (browserMarked) { clearStaffViewer(); setBrowserMarked(false) }
    else { markStaffViewer(); setBrowserMarked(true) }
  }

  const addStaffIp = async (ip, description) => {
    if (isDemoMode) return
    if (!ip?.trim()) return alert('IP address required')
    setIpBusy(true)
    try {
      const { error } = await supabase.from('staff_view_ips').insert({
        ip_address: ip.trim(),
        description: description?.trim() || null,
      })
      if (error) throw error
      setNewIp(''); setNewIpDesc('')
      await load()
    } catch (err) {
      alert(err.message || 'Could not save — is that IP already on the list?')
    } finally {
      setIpBusy(false)
    }
  }

  const removeStaffIp = async (id) => {
    if (!confirm('Remove this IP? Views from it will start counting again.')) return
    await supabase.from('staff_view_ips').delete().eq('id', id)
    await load()
  }

  const createCompanyLink = async () => {
    if (isDemoMode) return
    if (!newCompanyId) return alert('Pick a company first.')
    setCreatingCompany(true)
    try {
      const { data, error } = await supabase.rpc('create_preview_link', {
        p_company_id: newCompanyId,
        p_label: newCompanyLabel || null,
        p_days: 30,
      })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Failed to create link')
      setNewCompanyLabel('')
      await load()
    } catch (err) {
      alert(err.message || 'Failed to create link')
    } finally {
      setCreatingCompany(false)
    }
  }

  const createGroupLink = async () => {
    if (isDemoMode) return
    if (!newGroupId) return alert('Pick a client group first.')
    setCreatingGroup(true)
    try {
      const { data, error } = await supabase.rpc('create_group_preview_link', {
        p_group_id: newGroupId,
        p_label: newGroupLabel || null,
        p_days: 30,
      })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Failed to create link')
      setNewGroupLabel('')
      await load()
    } catch (err) {
      alert(err.message || 'Failed to create link')
    } finally {
      setCreatingGroup(false)
    }
  }

  const revoke = async (token) => {
    if (!confirm('Revoke this preview link? Anyone who has it will no longer be able to view.')) return
    await supabase.from('preview_links').update({ revoked_at: new Date().toISOString() }).eq('token', token)
    await load()
  }

  const extendLink = async (pl) => {
    if (isDemoMode) return
    const raw = prompt(
      `Extend "${pl.label || 'this link'}" by how many days?\n\n` +
      `Currently expires ${new Date(pl.expires_at).toLocaleDateString('en-GB')}.`,
      '30',
    )
    if (raw === null) return
    const days = parseInt(raw, 10)
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      return alert('Enter a whole number of days between 1 and 365.')
    }
    // Extend from today if it has already lapsed, otherwise from the existing
    // expiry — so extending a live link never shortens it.
    const base = new Date(Math.max(Date.now(), new Date(pl.expires_at).getTime()))
    base.setDate(base.getDate() + days)
    const { error } = await supabase.from('preview_links')
      .update({ expires_at: base.toISOString(), revoked_at: null })
      .eq('token', pl.token)
    if (error) return alert(error.message)
    await load()
    alert(`Extended. New expiry: ${base.toLocaleDateString('en-GB')}`)
  }

  const resetViews = async (token, label) => {
    if (!confirm(`Reset view counter for "${label || 'this link'}" to zero? Useful after you've been testing the link yourself and want a clean count before sharing.`)) return
    await supabase.from('preview_links')
      .update({ view_count: 0, last_viewed_at: null })
      .eq('token', token)
    await load()
  }

  const deleteLink = async (token, label) => {
    const msg = `Delete "${label || 'this preview link'}" permanently?\n\nThe URL will 404 for anyone who has it. View history and stats are lost too. Consider Revoke if you might want to see the stats later.`
    if (!confirm(msg)) return
    await supabase.from('preview_links').delete().eq('token', token)
    await load()
  }

  // Given a preview link, work out which company_ids the new user should be
  // linked to. Company-scoped preview = just that one site. Group-scoped =
  // every non-archived member of the group (Central-Team pattern).
  const resolveTargetCompanyIds = async (link) => {
    if (link.company_id) return [link.company_id]
    if (link.client_group_id) {
      const { data } = await supabase.from('companies')
        .select('id').eq('client_group_id', link.client_group_id).is('archived_at', null)
      return (data || []).map(c => c.id)
    }
    return []
  }

  const openGoLive = (link) => {
    setGoLive({ link, name: '', email: '', sending: false, result: null })
  }
  const closeGoLive = () => setGoLive(null)

  const sendGoLiveInvite = async () => {
    if (!goLive) return
    const email = (goLive.email || '').trim().toLowerCase()
    const name = (goLive.name || '').trim()
    if (!email) return alert('Enter an email address for the invite.')
    setGoLive(g => ({ ...g, sending: true, result: null }))
    try {
      const companyIds = await resolveTargetCompanyIds(goLive.link)
      if (companyIds.length === 0) throw new Error('This preview link is no longer linked to any active site.')
      const { data, error } = await supabase.functions.invoke('invite-users', {
        body: { users: [{ email, full_name: name, role: 'client', company_ids: companyIds }] },
      })
      if (error) throw error
      const row = data?.results?.[0]
      if (!row?.ok) throw new Error(row?.error || 'Invite failed')
      setGoLive(g => ({ ...g, sending: false, result: { ok: true, invited: row.invited, companies: row.companies } }))
    } catch (err) {
      setGoLive(g => ({ ...g, sending: false, result: { ok: false, error: err.message || 'Invite failed' } }))
    }
  }

  const revokeAndClose = async () => {
    if (!goLive?.link?.token) return closeGoLive()
    await supabase.from('preview_links')
      .update({ revoked_at: new Date().toISOString() })
      .eq('token', goLive.link.token)
    closeGoLive()
    await load()
  }

  const cleanupInactive = async () => {
    const inactive = links.filter(l => l.revoked_at || new Date(l.expires_at) < new Date())
    if (inactive.length === 0) return alert('Nothing to clean up — all links are still active.')
    if (!confirm(`Delete ${inactive.length} revoked/expired link${inactive.length === 1 ? '' : 's'} permanently? This can't be undone.`)) return
    await supabase.from('preview_links').delete().in('token', inactive.map(l => l.token))
    await load()
  }

  const copy = async (token) => {
    const url = `${window.location.origin}/p/${token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(t => (t === token ? null : t)), 2000)
    } catch {
      window.prompt('Copy this URL:', url)
    }
  }

  // Group links by target (group or company) for a legible hierarchy.
  // Group-scoped links live in their own section separate from any specific
  // site's links so the "head office" concept is visually distinct.
  const grouped = useMemo(() => {
    const buckets = new Map()
    for (const l of links) {
      const isGroup = !!l.client_group_id
      const key = isGroup
        ? `group::${l.client_groups?.name || '(deleted group)'}::${l.client_group_id}`
        : `company::${l.companies?.client_groups?.name || 'Ungrouped'}::${l.companies?.name || '(deleted)'}::${l.companies?.id || ''}`
      if (!buckets.has(key)) {
        buckets.set(key, {
          kind: isGroup ? 'group' : 'company',
          groupName: isGroup ? l.client_groups?.name : (l.companies?.client_groups?.name || 'Ungrouped'),
          companyName: isGroup ? null : l.companies?.name,
          links: [],
        })
      }
      buckets.get(key).links.push(l)
    }
    return [...buckets.values()].sort((a, b) => {
      if (a.groupName !== b.groupName) return (a.groupName || '').localeCompare(b.groupName || '')
      // group-scope entries sort above company-scope within the same group
      if (a.kind !== b.kind) return a.kind === 'group' ? -1 : 1
      return (a.companyName || '').localeCompare(b.companyName || '')
    })
  }, [links])

  return (
    <div className="space-y-4 max-w-5xl">
      <DemoNotice />

      {/* Single-site preview */}
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-brand" /> Single-site preview link
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Preview one specific site. The recipient sees that site's portal — logo, products, order
          history — in read-only demo mode. Ideal for pitching a specific location or getting a
          site manager's feedback.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_auto] gap-2">
          <select value={newCompanyId} onChange={e => setNewCompanyId(e.target.value)} className="input-field">
            <option value="">— pick a site —</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>
                {c.client_groups?.name ? `${c.client_groups.name} · ` : ''}{c.name}
              </option>
            ))}
          </select>
          <input
            type="text" value={newCompanyLabel} onChange={e => setNewCompanyLabel(e.target.value)}
            placeholder="Optional label (e.g. 'John Underhill demo')"
            className="input-field" maxLength={80}
          />
          <button onClick={createCompanyLink} disabled={creatingCompany || !newCompanyId} className="btn-primary whitespace-nowrap">
            <Plus className="w-4 h-4" /> {creatingCompany ? 'Creating…' : 'Create link'}
          </button>
        </div>
      </div>

      {/* Group / head office preview */}
      <div className="card border-l-4 border-brand">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Landmark className="w-5 h-5 text-brand" /> Head office preview link <span className="text-xs font-normal text-gray-500">· whole group</span>
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          For clients with multiple sites (e.g. Marstons, Away Resorts, Travel Chapter).
          The recipient sees the portal in <strong>All Sites</strong> mode with a site picker,
          combined My Orders across every site, and a group-wide Reports view. Perfect for
          pitching to a Head of Purchasing at a group's head office.
        </p>
        {groups.length === 0 ? (
          <p className="text-xs text-gray-400 italic">
            No client groups with 2+ sites yet. Add a group and link sites to it before creating a head-office preview.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_auto] gap-2">
            <select value={newGroupId} onChange={e => setNewGroupId(e.target.value)} className="input-field">
              <option value="">— pick a client group —</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name} · {g.companies.length} sites</option>
              ))}
            </select>
            <input
              type="text" value={newGroupLabel} onChange={e => setNewGroupLabel(e.target.value)}
              placeholder="Optional label (e.g. 'Head office demo — Prashanna')"
              className="input-field" maxLength={80}
            />
            <button onClick={createGroupLink} disabled={creatingGroup || !newGroupId} className="btn-primary whitespace-nowrap">
              <Plus className="w-4 h-4" /> {creatingGroup ? 'Creating…' : 'Create link'}
            </button>
          </div>
        )}
      </div>

      {!loading && links.some(l => l.revoked_at || new Date(l.expires_at) < new Date()) && (
        <div className="flex items-center justify-end">
          <button
            onClick={cleanupInactive}
            title="Delete every revoked or expired link in one go"
            className="text-xs px-3 py-1.5 rounded-md text-gray-600 hover:bg-gray-100 border border-gray-200 inline-flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clean up revoked / expired
          </button>
        </div>
      )}

      {/* Staff IP allowlist — views from these IPs don't count towards
          preview-link stats, so Simon's own testing doesn't inflate what
          looks like customer engagement. */}
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="w-4 h-4 text-brand" />
          <h3 className="font-semibold text-gray-800">Staff IPs (views from these don't count)</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Any IP address on this list is treated as "internal testing" — visits from it are excluded from the view counter on every preview link. Add your office / VPN / home IPs so the numbers on shared links reflect real customer engagement only.
        </p>

        {/* Browser-level suppression. More reliable than the IP list: it follows
            the person rather than the address, so it keeps working from home,
            a phone, or anywhere the IP isn't known in advance. */}
        <div className="flex items-start gap-3 p-3 mb-4 rounded-lg border border-gray-200 bg-gray-50/70">
          <Eye className={`w-5 h-5 shrink-0 mt-0.5 ${browserMarked ? 'text-brand-dark' : 'text-gray-400'}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800">
              This browser {browserMarked ? 'is marked as staff' : 'is not marked'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {browserMarked
                ? 'Preview views from this browser are excluded wherever you open them — office, home or phone. No IP needed.'
                : 'Views from this browser are being counted. Marking it excludes them without needing to know your IP.'}
            </p>
          </div>
          <button
            onClick={toggleBrowserMark}
            className={`whitespace-nowrap text-xs ${browserMarked ? 'btn-secondary' : 'btn-primary'}`}
          >
            {browserMarked ? 'Unmark' : 'Mark this browser'}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
          <label>
            <span className="text-xs text-gray-500 block mb-1">IP address</span>
            <input
              type="text"
              value={newIp}
              onChange={e => setNewIp(e.target.value)}
              placeholder="e.g. 82.145.14.28"
              className="input-field"
            />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Note (optional)</span>
            <input
              type="text"
              value={newIpDesc}
              onChange={e => setNewIpDesc(e.target.value)}
              placeholder="Office / Simon home / VPN"
              className="input-field"
            />
          </label>
          <button onClick={() => addStaffIp(newIp, newIpDesc)} disabled={ipBusy || !newIp.trim()} className="btn-primary whitespace-nowrap">
            <Plus className="w-4 h-4" /> Add
          </button>
          {detectedIp && !staffIps.some(s => s.ip_address === detectedIp) && (
            <button
              onClick={() => addStaffIp(detectedIp, 'Auto-added (your current IP)')}
              disabled={ipBusy}
              className="btn-secondary whitespace-nowrap text-xs"
              title={`Your current public IP is ${detectedIp}`}
            >
              + This IP ({detectedIp})
            </button>
          )}
        </div>

        {staffIps.length === 0 ? (
          <p className="text-xs text-gray-400 italic mt-3">No staff IPs on the list yet — every view is being counted.</p>
        ) : (
          <ul className="divide-y divide-gray-100 mt-4 border border-gray-100 rounded-lg overflow-hidden">
            {staffIps.map(s => (
              <li key={s.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                <code className="font-mono text-gray-800 min-w-[130px]">{s.ip_address}</code>
                <span className="text-gray-500 flex-1 truncate">{s.description || '—'}</span>
                <span className="text-xs text-gray-400 whitespace-nowrap">added {new Date(s.added_at).toLocaleDateString()}</span>
                <button
                  onClick={() => removeStaffIp(s.id)}
                  className="text-xs text-red-600 hover:bg-red-50 rounded px-2 py-1"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {loading && <div className="card"><p className="text-sm text-gray-500">Loading…</p></div>}

      {!loading && grouped.length === 0 && (
        <div className="card">
          <p className="text-sm text-gray-500 text-center py-6">
            No preview links yet — create one above.
          </p>
        </div>
      )}

      {grouped.map(g => (
        <div key={`${g.kind}-${g.groupName}-${g.companyName || ''}`} className={`card ${g.kind === 'group' ? 'border-l-4 border-brand' : ''}`}>
          <div className="flex items-center gap-2 mb-3">
            {g.kind === 'group'
              ? <Landmark className="w-4 h-4 text-brand" />
              : <Layers className="w-4 h-4 text-brand" />
            }
            <h3 className="font-semibold text-gray-800">
              {g.kind === 'group' ? `${g.groupName} — Head office` : g.companyName}
            </h3>
            <span className="text-xs text-gray-400">
              · {g.kind === 'group' ? 'group scope' : g.groupName} · {g.links.length} link{g.links.length === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="divide-y divide-gray-100">
            {g.links.map(pl => {
              const url = `${window.location.origin}/p/${pl.token}`
              const revoked = !!pl.revoked_at
              const expired = new Date(pl.expires_at) < new Date()
              const active = !revoked && !expired
              return (
                <li key={pl.token} className="py-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 text-sm truncate">
                        {pl.label || 'Untitled preview'}
                        {revoked && <span className="ml-2 text-xs text-red-600 font-normal">· revoked</span>}
                        {!revoked && expired && <span className="ml-2 text-xs text-amber-600 font-normal">· expired</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate font-mono">{url}</div>
                      <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Expires {new Date(pl.expires_at).toLocaleDateString()}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          {pl.view_count} view{pl.view_count === 1 ? '' : 's'}
                          {pl.last_viewed_at && ` · last ${new Date(pl.last_viewed_at).toLocaleString()}`}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {active && (
                        <button
                          onClick={() => copy(pl.token)}
                          className="text-xs px-2 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
                        >
                          {copiedToken === pl.token
                            ? <><Check className="w-3.5 h-3.5 text-emerald-500" /> Copied</>
                            : <><Copy className="w-3.5 h-3.5" /> Copy URL</>
                          }
                        </button>
                      )}
                      {active && (
                        <a
                          href={url} target="_blank" rel="noopener"
                          className="text-xs px-2 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
                        >
                          <Eye className="w-3.5 h-3.5" /> Open
                        </a>
                      )}
                      {active && (
                        <button
                          onClick={() => openGoLive(pl)}
                          title="Convert this preview into a real client account"
                          className="text-xs px-2 py-1 rounded-md border border-brand text-brand-dark hover:bg-brand-50 inline-flex items-center gap-1 font-medium"
                        >
                          <UserPlus className="w-3.5 h-3.5" /> Go live
                        </button>
                      )}
                      <button
                        onClick={() => extendLink(pl)}
                        title={revoked ? 'Extend and un-revoke this link' : 'Push the expiry date back'}
                        className="text-xs px-2 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
                      >
                        <Calendar className="w-3.5 h-3.5" /> Extend
                      </button>
                      {active && pl.view_count > 0 && (
                        <button
                          onClick={() => resetViews(pl.token, pl.label)}
                          title="Zero the view counter (useful after your own testing)"
                          className="text-xs px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100 inline-flex items-center gap-1"
                        >
                          <TrendingUp className="w-3.5 h-3.5" /> Reset views
                        </button>
                      )}
                      {!revoked && (
                        <button
                          onClick={() => revoke(pl.token)}
                          className="text-xs px-2 py-1 rounded-md text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Revoke
                        </button>
                      )}
                      <button
                        onClick={() => deleteLink(pl.token, pl.label)}
                        title="Delete permanently (URL becomes 404)"
                        className="text-xs px-2 py-1 rounded-md text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      ))}

      {goLive && (
        <GoLiveModal
          state={goLive}
          onChange={(patch) => setGoLive(g => ({ ...g, ...patch }))}
          onClose={closeGoLive}
          onSubmit={sendGoLiveInvite}
          onRevokeAndClose={revokeAndClose}
          onGoBulk={() => { closeGoLive(); navigate('/admin/import-clients') }}
        />
      )}
    </div>
  )
}

// Convert-to-real-client modal. Sends a single branded invite for the target
// site (or group) using the same invite-users Edge Function the bulk XLSX
// flow uses. Offers to revoke the source preview link once the invite is out
// — the prospect is now a real user, so the preview URL is redundant.
function GoLiveModal({ state, onChange, onClose, onSubmit, onRevokeAndClose, onGoBulk }) {
  const { link, name, email, sending, result } = state
  const isGroup = !!link.client_group_id
  const targetName = isGroup ? link.client_groups?.name : link.companies?.name
  const targetSubtitle = isGroup
    ? `Head office / group — the new user will be linked to every site in ${targetName}`
    : `Single site — the new user will be linked to just ${targetName}`

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-brand" />
            <h3 className="font-semibold text-gray-800">Go live — invite as real client</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!result && (
            <>
              <div className="rounded-lg bg-brand-50 border border-brand/20 px-3 py-2">
                <div className="text-xs uppercase tracking-wide text-brand-dark font-semibold">Target</div>
                <div className="text-sm font-medium text-gray-800 mt-1">{targetName}</div>
                <div className="text-xs text-gray-600 mt-0.5">{targetSubtitle}</div>
              </div>

              <p className="text-sm text-gray-600">
                Quick single-user invite. They'll get a branded email, set a password, and log into
                the real portal — no more preview restrictions. For more than one user, use{' '}
                <button onClick={onGoBulk} className="text-brand-dark underline hover:text-brand">
                  Import Clients (XLSX)
                </button>.
              </p>

              <label className="block">
                <span className="text-xs text-gray-500 block mb-1">Their name</span>
                <input
                  type="text"
                  value={name}
                  onChange={e => onChange({ name: e.target.value })}
                  placeholder="e.g. John Underhill"
                  className="input-field"
                  disabled={sending}
                  autoFocus
                />
              </label>

              <label className="block">
                <span className="text-xs text-gray-500 block mb-1">Their email</span>
                <input
                  type="email"
                  value={email}
                  onChange={e => onChange({ email: e.target.value })}
                  placeholder="e.g. john.underhill@marstons.co.uk"
                  className="input-field"
                  disabled={sending}
                />
              </label>
            </>
          )}

          {result?.ok && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1">
                <Check className="w-5 h-5" /> Invite sent
              </div>
              <p className="text-sm text-emerald-900">
                {result.invited
                  ? `Branded invite email is on its way to ${email}. They'll set a password and log in with client access to ${result.companies} site${result.companies === 1 ? '' : 's'}.`
                  : `${email} already had an account — we just linked them to ${result.companies} site${result.companies === 1 ? '' : 's'}. They can log in with their existing password.`}
              </p>
              <p className="text-xs text-emerald-800 mt-3">
                The preview link is now redundant. Revoke it below to keep the list tidy.
              </p>
            </div>
          )}

          {result && !result.ok && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              {result.error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          {!result && (
            <>
              <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
              <button
                onClick={onSubmit}
                disabled={sending || !email.trim()}
                className="btn-primary text-sm"
              >
                <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send invite'}
              </button>
            </>
          )}
          {result?.ok && (
            <>
              <button onClick={onClose} className="btn-secondary text-sm">Keep preview link</button>
              <button onClick={onRevokeAndClose} className="btn-primary text-sm">
                <XCircle className="w-4 h-4" /> Revoke preview & close
              </button>
            </>
          )}
          {result && !result.ok && (
            <>
              <button onClick={onClose} className="btn-secondary text-sm">Close</button>
              <button onClick={() => onChange({ result: null })} className="btn-primary text-sm">
                <ArrowRight className="w-4 h-4" /> Try again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
